/* eslint-disable max-len */
import React from 'react';
import {
    Box,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
    Select,
    Alert,
    Checkbox,
} from '@mui/material';
import ActionButtonsFormularioAgricultura from 'components/forms/ActionButtonsFormularioAgricultura';
import { ContainerForm } from 'components/shared/container_form';
import InputComLabel from '../../../../../../components/shared/custom_input_label';
import { useFormularioSecretariaMunicipalAgriculturaStore } from '../../../../../../stores/forms/formularioAgricultura.store';

const CURSOS_MEDIO_TECNICO = [
    'Administração',
    'Agricultura',
    'Agrimensura, Geomensura ou Topografia',
    'Agroecologia',
    'Agroindústria',
    'Agropecuária',
    'Alimentos ou Indústria de Alimentos',
    'Ambiental ou Meio Ambiente',
    'Construção Civil, Obras ou Estradas',
    'Contabilidade ou Finanças',
    'Controle de Qualidade',
    'Desenho Técnico',
    'Educação',
    'Elétrica, Eletrotécnica ou Eletrônica',
    'Florestal',
    'Geoprocessamento, Geodésia ou Cartografia',
    'Hidrologia',
    'Industrial',
    'Máquinas ou Mecânica',
    'Mineração',
    'Pecuária',
    'Piscicultura e Aquicultura',
    'Prevenção e combate a incêndios',
    'Química',
    'Saúde',
    'Secretariado',
    'Segurança do Trabalho',
    'Serviço Social',
    'Tecnologia da Informação ou de Computadores',
    'Transporte',
    'Turismo',
    'Veterinária',
    'Zootecnia',
];

const CURSOS_SUPERIOR = [
    'Administração',
    'Agronomia ou Engenharia Agronômica',
    'Arquitetura e Urbanismo',
    'Biblioteconomia ou Arquivologia',
    'Biologia ou Ciências Biológicas',
    'Ciências Contábeis',
    'Ciência da Computação ou Análise de Sistemas',
    'Ciências Sociais',
    'Comunicação Social ou Jornalismo',
    'Direito',
    'Economia ou Ciências Econômicas',
    'Engenharia Agrícola',
    'Engenharia Ambiental',
    'Engenharia Sanitária e Ambiental',
    'Engenharia Civil',
    'Engenharia de Agrimensura e/ou Cartográfica',
    'Engenharia de Alimentos',
    'Engenharia Elétrica',
    'Engenharia Florestal',
    'Engenharia (Outra)',
    'Geografia',
    'Geologia',
    'Geoprocessamento',
    'Gestão Ambiental',
    'Gestão Pública',
    'História',
    'Letras',
    'Medicina Veterinária',
    'Nutrição',
    'Pedagogia',
    'Secretariado Executivo',
    'Serviço Social',
    'Turismo',
];

const TIPOS_VINCULO = [
    'Efetivo',
    'Efetivo com cargo comissionado',
    'Contratado temporariamente',
    'Exclusivamente comissionado',
    'Cedido por outra instituição',
];

const Step6: React.FC = () => {
    const { formulario, setPartialForm } =
        useFormularioSecretariaMunicipalAgriculturaStore();

    // Função para aplicar máscara de telefone
    const mascaraTelefone = (valor: string) => {
        const valorLimpo = valor.replace(/\D/g, '');
        return valorLimpo.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    };

    // Função para validar email
    const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Função para atualizar servidor específico
    const updateServidor = (index: number, campo: string, valor: any) => {
        const servidores = formulario?.quadro_servidores_secretaria_lista || [];
        const novoServidor = { ...servidores[index], [campo]: valor };
        const novosServidores = [...servidores];
        novosServidores[index] = novoServidor;
        setPartialForm('quadro_servidores_secretaria_lista', novosServidores);
    };

    // Função para inicializar servidores baseado na quantidade
    const initializeServidores = (quantidade: number) => {
        const servidores = [];
        for (let i = 0; i < quantidade; i++) {
            servidores.push({
                nome: '',
                tipo_vinculo: '',
                telefone: '',
                grau_escolaridade: [],
                email: '',
                medio_tecnico_concluido: '',
                superior_concluido: '',
            });
        }
        setPartialForm('quadro_servidores_secretaria_lista', servidores);
    };

    // Atualizar servidores quando a quantidade mudar
    React.useEffect(() => {
        const quantidade = formulario?.quadro_servidores_secretaria_quantidade || 0;
        if (quantidade > 0 && (!formulario?.quadro_servidores_secretaria_lista || formulario.quadro_servidores_secretaria_lista.length !== quantidade)) {
            initializeServidores(quantidade);
        }
    }, [formulario?.quadro_servidores_secretaria_quantidade]);

    // Validações
    const quantidadeValida = (formulario?.quadro_servidores_secretaria_quantidade || 0) > 0;
    const quantidadeConcursadosValida =
        (formulario?.quadro_servidores_secretaria_concursados_quantidade || 0) >= 0 &&
        (formulario?.quadro_servidores_secretaria_concursados_quantidade || 0) <=
        (formulario?.quadro_servidores_secretaria_quantidade || 0);

    const servidoresPreenchidos = formulario?.quadro_servidores_secretaria_lista?.every(servidor => {
        return (
            servidor.nome.length > 0 &&
            servidor.tipo_vinculo.length > 0 &&
            servidor.telefone.length >= 15 && // Considerando máscara completa
            servidor.grau_escolaridade.length > 0 &&
            regexEmail.test(servidor.email) &&
            (!servidor.grau_escolaridade.includes('Médio-Técnico') || servidor.medio_tecnico_concluido.length > 0) &&
            (!servidor.grau_escolaridade.includes('Superior') || servidor.superior_concluido.length > 0)
        );
    }) || false;

    const podeAvancar = quantidadeValida && quantidadeConcursadosValida && servidoresPreenchidos;

    return (
        <>
            <ContainerForm title='Quadro de Servidores'>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Nesta aba, será exibida a quantidade de cadastros conforme o que foi informado na aba &apos;Informações Complementares&apos;.
                    Por exemplo: se você preencheu o número 5, aparecerão 5 espaços para cadastro, ou seja, será possível cadastrar 5 servidores.
                </Alert>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} gap={4} display={'flex'} flexDirection={'column'}>

                    {/* Quantidade de Servidores */}
                    <InputComLabel
                        valor={formulario?.quadro_servidores_secretaria_quantidade?.toString() || ''}
                        setValor={(valor) => {
                            const num = parseInt(valor) || 0;
                            setPartialForm('quadro_servidores_secretaria_quantidade', num);
                            if (num === 0) {
                                setPartialForm('quadro_servidores_secretaria_concursados_quantidade', 0);
                                setPartialForm('quadro_servidores_secretaria_lista', []);
                            }
                        }}
                        labelTexto='Quantos servidores/servidoras compõem o quadro funcional dessa secretaria ou Órgão Municipal?'
                        placeholder='Digite o número de servidores'
                        type='number'
                        required
                    />

                    {/* Quantidade de Concursados */}
                    {quantidadeValida && (
                        <>
                            <InputComLabel
                                valor={formulario?.quadro_servidores_secretaria_concursados_quantidade?.toString() || ''}
                                setValor={(valor) => {
                                    const num = parseInt(valor) || 0;
                                    if (num <= (formulario?.quadro_servidores_secretaria_quantidade || 0)) {
                                        setPartialForm('quadro_servidores_secretaria_concursados_quantidade', num);
                                    }
                                }}
                                labelTexto={`Quantos desses servidores/servidoras são concursados(as)? (Máximo: ${formulario?.quadro_servidores_secretaria_quantidade})`}
                                placeholder='Digite o número de concursados'
                                type='number'
                                required
                            />
                            {!quantidadeConcursadosValida && (
                                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                    O número não pode ser maior que o total de servidores
                                </Typography>
                            )}
                        </>
                    )}

                    {/* Formulários dos Servidores */}
                    {quantidadeValida && quantidadeConcursadosValida && formulario?.quadro_servidores_secretaria_lista?.map((servidor, index) => (
                        <Box key={index} sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            padding: '16px',
                            marginTop: '16px'
                        }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                                Servidor {index + 1}
                            </Typography>

                            {/* Nome do Servidor */}
                            <InputComLabel
                                valor={servidor.nome || ''}
                                setValor={(valor) => updateServidor(index, 'nome', valor)}
                                labelTexto='Nome do Servidor'
                                placeholder='Nome completo do servidor'
                                required
                            />

                            {/* Grau de Escolaridade */}
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <FormLabel sx={{ fontWeight: 'bold', color: '#333' }} component="legend" required>
                                    Grau de Escolaridade
                                </FormLabel>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={servidor.grau_escolaridade.includes('Fundamental')}
                                                onChange={(e) => {
                                                    const novaEscolaridade: string[] = [];
                                                    if (e.target.checked) {
                                                        if (!novaEscolaridade.includes('Fundamental')) {
                                                            novaEscolaridade.push('Fundamental');
                                                        }
                                                    } else {
                                                        const index = novaEscolaridade.indexOf('Fundamental');
                                                        if (index > -1) {
                                                            novaEscolaridade.splice(index, 1);
                                                        }
                                                    }
                                                    updateServidor(index, 'grau_escolaridade', novaEscolaridade);
                                                }}
                                            />
                                        }
                                        label="Fundamental"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={servidor.grau_escolaridade.includes('Médio')}
                                                onChange={(e) => {
                                                    const novaEscolaridade: string[] = [];
                                                    if (e.target.checked) {
                                                        if (!novaEscolaridade.includes('Médio')) {
                                                            novaEscolaridade.push('Médio');
                                                        }
                                                    } else {
                                                        const index = novaEscolaridade.indexOf('Médio');
                                                        if (index > -1) {
                                                            novaEscolaridade.splice(index, 1);
                                                        }
                                                    }
                                                    updateServidor(index, 'grau_escolaridade', novaEscolaridade);
                                                }}
                                            />
                                        }
                                        label="Médio"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={servidor.grau_escolaridade.includes('Médio-Técnico')}
                                                onChange={(e) => {
                                                    const novaEscolaridade: string[] = [];
                                                    if (e.target.checked) {
                                                        if (!novaEscolaridade.includes('Médio-Técnico')) {
                                                            novaEscolaridade.push('Médio-Técnico');
                                                        }
                                                    } else {
                                                        const index = novaEscolaridade.indexOf('Médio-Técnico');
                                                        if (index > -1) {
                                                            novaEscolaridade.splice(index, 1);
                                                        }
                                                        // Limpar campo condicional quando desmarcar
                                                        updateServidor(index, 'medio_tecnico_concluido', '');
                                                    }
                                                    updateServidor(index, 'grau_escolaridade', novaEscolaridade);
                                                }}
                                            />
                                        }
                                        label="Médio-Técnico"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={servidor.grau_escolaridade.includes('Superior')}
                                                onChange={(e) => {
                                                    const novaEscolaridade: string[] = [];
                                                    if (e.target.checked) {
                                                        if (!novaEscolaridade.includes('Superior')) {
                                                            novaEscolaridade.push('Superior');
                                                        }
                                                    } else {
                                                        const index = novaEscolaridade.indexOf('Superior');
                                                        if (index > -1) {
                                                            novaEscolaridade.splice(index, 1);
                                                        }
                                                        // Limpar campo condicional quando desmarcar
                                                        updateServidor(index, 'superior_concluido', '');
                                                    }
                                                    updateServidor(index, 'grau_escolaridade', novaEscolaridade);
                                                }}
                                            />
                                        }
                                        label="Superior"
                                    />
                                </Box>
                            </FormControl>

                            {/* Lista condicional para Médio-Técnico */}
                            {servidor.grau_escolaridade.includes('Médio-Técnico') && (
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <FormLabel sx={{ fontWeight: 'bold', color: '#333' }} required>Curso Médio-Técnico</FormLabel>
                                    <Select
                                        value={servidor.medio_tecnico_concluido || ''}
                                        onChange={(e) => updateServidor(index, 'medio_tecnico_concluido', e.target.value)}
                                        displayEmpty
                                        required
                                    >
                                        <MenuItem value="">Selecione o curso</MenuItem>
                                        {CURSOS_MEDIO_TECNICO.map((curso) => (
                                            <MenuItem key={curso} value={curso}>{curso}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {/* Lista condicional para Superior */}
                            {servidor.grau_escolaridade.includes('Superior') && (
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <FormLabel required>Curso Superior</FormLabel>
                                    <Select
                                        value={servidor.superior_concluido || ''}
                                        onChange={(e) => updateServidor(index, 'superior_concluido', e.target.value)}
                                        displayEmpty
                                        required
                                    >
                                        <MenuItem value="">Selecione o curso</MenuItem>
                                        {CURSOS_SUPERIOR.map((curso) => (
                                            <MenuItem key={curso} value={curso}>{curso}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {/* Tipo de Vínculo */}
                            <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                                <FormLabel sx={{ fontWeight: 'bold', color: '#333' }} component="legend" required>
                                    Tipo de Vínculo
                                </FormLabel>
                                <RadioGroup
                                    value={servidor.tipo_vinculo || ''}
                                    onChange={(e) => updateServidor(index, 'tipo_vinculo', e.target.value)}
                                >
                                    {TIPOS_VINCULO.map((tipo) => (
                                        <FormControlLabel key={tipo} value={tipo} control={<Radio />} label={tipo} />
                                    ))}
                                </RadioGroup>
                            </FormControl>

                            {/* Telefone */}
                            <InputComLabel
                                valor={mascaraTelefone(servidor.telefone || '')}
                                setValor={(valor) => updateServidor(index, 'telefone', mascaraTelefone(valor))}
                                labelTexto='Telefone'
                                placeholder='(99) 99999-9999'
                                maxLength={15}
                                required
                            />

                            <Box sx={{
                                mt: 1
                            }}>
                                {/* Email */}
                                <InputComLabel
                                    valor={servidor.email || ''}
                                    setValor={(valor) => updateServidor(index, 'email', valor)}
                                    labelTexto='E-mail'
                                    placeholder='exemplo@email.com'
                                    type='email'
                                    required
                                />
                                {servidor.email.length > 0 && !regexEmail.test(servidor.email) && (
                                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                                        Formato de email inválido
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                <ActionButtonsFormularioAgricultura disabled={!podeAvancar} />
            </ContainerForm>
        </>
    );
};

export default Step6; 